/**
 * Phase 5 — Buyer quote detail with real conversation thread.
 *
 * Surface:
 *   - Status pill + short ref + back link
 *   - Rich quote-response card (with WORKING Accept / Ask / Decline buttons)
 *   - "Your submitted request" collapsible summary
 *   - Conversation thread (operator messages NEVER expose operator name —
 *     always rendered as "ShutterKART Support")
 *   - Sticky bottom composer (textarea + attach + send)
 *
 * Live updates via WebSocket; polling fallback when WS is unavailable.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ChevronDown, MessageCircle, Paperclip, Send,
  X, FileText, CheckCircle2, AlertCircle, CalendarClock,
} from "lucide-react";

import {
  getMyQuote,
  acceptQuote,
  declineQuote,
  askQuoteQuestion,
  listConversationMessages,
  postConversationMessage,
  uploadConversationAttachment,
  getStoredAccessToken,
  absoluteUrl,
} from "../lib/api";
import { useBuyerAuth } from "../lib/buyer-auth";
import { createConversationWS } from "../lib/ws-client";
import ManageDataDrawer from "../components/buyer/ManageDataDrawer";

const STATUS_STYLES = {
  submitted: "bg-[#1A1A1A]/5 text-[#1A1A1A]/70",
  under_review: "bg-[#4A6FA5]/10 text-[#4A6FA5]",
  quote_sent: "bg-[#C8A24B]/15 text-[#9A7A2F]",
  conversation_ongoing: "bg-[#4A6FA5]/10 text-[#4A6FA5]",
  accepted: "bg-[#4F8060]/10 text-[#4F8060]",
  confirmed_offline: "bg-[#4F8060]/10 text-[#4F8060]",
  declined: "bg-[#B0413E]/10 text-[#B0413E]",
  expired: "bg-[#1A1A1A]/5 text-[#1A1A1A]/50",
};

function formatINR(n) {
  if (n == null) return "–";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDateLong(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return "";
  }
}

function formatTimeShort(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function dateBucket(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(Date.now() - 86400000);
  const same = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (same(d, today)) return "Today";
  if (same(d, yest)) return "Yesterday";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function isPast(iso) {
  if (!iso) return false;
  try {
    return new Date(iso).getTime() < Date.now();
  } catch {
    return false;
  }
}

export default function QuoteDetail() {
  const { id } = useParams();
  const { isAuthenticated } = useBuyerAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Load quote once
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/track?next=/quotes/${id}`, { replace: true });
      return undefined;
    }
    let cancelled = false;
    getMyQuote(id)
      .then((d) => {
        if (!cancelled) setQuote(d);
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        setError(
          status === 404 || status === 403
            ? "This request couldn't be found."
            : "Couldn't load this request.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [id, isAuthenticated, navigate]);

  const refreshQuote = useCallback(async () => {
    try {
      const d = await getMyQuote(id);
      setQuote(d);
    } catch {
      /* noop */
    }
  }, [id]);

  const shortRef =
    quote?.short_ref ||
    (id ? `SK-${id.replace(/-/g, "").slice(-8).toUpperCase()}` : "");
  const statusPill =
    STATUS_STYLES[quote?.status] || STATUS_STYLES.submitted;
  const payload = quote?.payload || {};
  const scope = payload?.scope || {};
  const refs = quote?.reference_image_ids || [];
  const response = quote?.quote_response;

  return (
    <section
      data-testid="quote-detail"
      className="mx-auto flex min-h-[100svh] max-w-3xl flex-col px-5 pb-[180px] pt-8 sm:px-6 sm:pt-14"
    >
      <Link
        to="/track"
        data-testid="quote-detail-back"
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55 hover:text-[#1A1A1A]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to my requests
      </Link>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <span
          data-testid="quote-detail-status"
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] ${statusPill}`}
        >
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
          {(quote?.status || "submitted").replaceAll("_", " ")}
        </span>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/45">
          {shortRef}
        </span>
      </div>
      <h1 className="mt-4 font-display text-3xl capitalize text-[#1A1A1A] sm:text-4xl">
        {(quote?.occasion || "your shoot").replaceAll("_", " ")}
      </h1>
      <p className="mt-1 text-sm text-[#1A1A1A]/65">
        {quote?.date_summary} · {quote?.location_summary}
      </p>

      {error ? (
        <p className="mt-8 rounded-2xl bg-[#B0413E]/5 px-4 py-3 text-sm text-[#B0413E]">
          {error}
        </p>
      ) : null}

      {response ? (
        <BuyerQuoteResponseCard
          response={response}
          quote={quote}
          onChanged={refreshQuote}
        />
      ) : null}

      <CollapsibleSummary
        open={summaryOpen}
        setOpen={setSummaryOpen}
        quote={quote}
        scope={scope}
        refs={refs}
        payload={payload}
      />

      {quote ? (
        <ConversationThread
          quote={quote}
          conversationId={quote.conversation_id}
          onSendShouldRefreshQuote={refreshQuote}
        />
      ) : null}

      {quote ? (
        <ManageDataDrawer
          quoteId={quote.id}
          onDeleted={refreshQuote}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Rich quote-response card (Accept / Ask / Decline)
// ---------------------------------------------------------------------------
function BuyerQuoteResponseCard({ response, quote, onChanged }) {
  const [accepting, setAccepting] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [acceptError, setAcceptError] = useState("");
  const validUntil = response.valid_until || null;
  const validUntilLabel = formatDateLong(validUntil);
  const expired = isPast(validUntil);
  const acted =
    quote?.status === "accepted" ||
    quote?.status === "declined" ||
    quote?.status === "expired" ||
    quote?.status === "confirmed_offline";

  async function onAccept() {
    setAcceptError("");
    setAccepting(true);
    try {
      const res = await acceptQuote(quote.id);
      setAcceptOpen(false);
      await onChanged?.();
      // Soft success message — stays in card
      window.scrollTo({ top: 0, behavior: "smooth" });
      return res;
    } catch (e) {
      setAcceptError(
        e?.response?.data?.detail || "Couldn't accept this quote right now.",
      );
    } finally {
      setAccepting(false);
    }
  }

  const onAskFocus = () => {
    const el = document.getElementById("buyer-composer-input");
    if (el) {
      el.focus();
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <section
      data-testid="quote-response-card"
      className="mt-8 overflow-hidden rounded-3xl border border-[#C8A24B]/30 bg-white shadow-sm"
    >
      <div className="bg-[#C8A24B]/15 px-6 py-5 sm:px-7">
        <p className="text-[10px] uppercase tracking-[0.32em] text-[#9A7A2F]">
          Your tailored quote
        </p>
        <p
          data-testid="quote-response-total"
          className="mt-2 font-display text-3xl text-[#1A1A1A] sm:text-4xl"
        >
          {formatINR(response.total_estimate)}
        </p>
        {validUntilLabel ? (
          <p
            data-testid="quote-response-valid-until"
            className={`mt-1 inline-flex items-center gap-1.5 text-xs ${
              expired ? "text-[#B0413E]" : "text-[#1A1A1A]/60"
            }`}
          >
            <CalendarClock className="h-3.5 w-3.5" />
            {expired ? `Expired on ${validUntilLabel}` : `Valid until ${validUntilLabel}`}
          </p>
        ) : null}
        {response.tax_breakup ? (
          <BuyerTaxBreakdown tb={response.tax_breakup} />
        ) : null}
      </div>

      <div className="space-y-2 px-6 py-5 text-sm sm:px-7">
        {(response.line_items || []).map((li, i) => (
          <div key={i} className="flex justify-between gap-3">
            <span className="text-[#1A1A1A]">
              {li.title} × {li.quantity}
            </span>
            <span className="tabular-nums text-[#1A1A1A]">
              {formatINR(li.line_total)}
            </span>
          </div>
        ))}
        {response.discount_amount ? (
          <div className="flex justify-between text-[#1A1A1A]/55">
            <span>Discount</span>
            <span className="tabular-nums">-{formatINR(response.discount_amount)}</span>
          </div>
        ) : null}
      </div>

      {Array.isArray(response.deliverables) && response.deliverables.length ? (
        <div className="border-t border-[#1A1A1A]/10 px-6 py-5 sm:px-7">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#9A7A2F]">
            What you get
          </p>
          <ul className="mt-2 space-y-1 text-sm text-[#1A1A1A]">
            {response.deliverables.map((d, i) => (
              <li key={i}>• {d}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {response.payment_terms_note ? (
        <div className="border-t border-[#1A1A1A]/10 px-6 py-4 text-xs text-[#1A1A1A]/65 sm:px-7">
          {response.payment_terms_note}
        </div>
      ) : null}

      {acted ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-[#1A1A1A]/10 bg-[#F5F1E8] px-6 py-4 text-xs sm:px-7">
          {quote.status === "accepted" || quote.status === "confirmed_offline" ? (
            <span
              data-testid="quote-response-accepted-pill"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#4F8060]/15 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#4F8060]"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Accepted{quote.accepted_at ? ` on ${formatDateLong(quote.accepted_at)}` : ""}
            </span>
          ) : null}
          {quote.status === "declined" ? (
            <span
              data-testid="quote-response-declined-pill"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#B0413E]/15 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#B0413E]"
            >
              <X className="h-3.5 w-3.5" />
              Declined{quote.declined_at ? ` on ${formatDateLong(quote.declined_at)}` : ""}
            </span>
          ) : null}
          {quote.status === "expired" ? (
            <span
              data-testid="quote-response-expired-pill"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1A1A1A]/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/60"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              This quote has expired
            </span>
          ) : null}
          <span className="text-[#1A1A1A]/60">
            Our team will reach out shortly to confirm payment and next steps.
          </span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 border-t border-[#1A1A1A]/10 bg-[#F5F1E8] px-6 py-4 sm:px-7">
          <button
            type="button"
            data-testid="quote-response-accept"
            disabled={expired}
            onClick={() => setAcceptOpen(true)}
            className="rounded-full bg-[#1A1A1A] px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] text-[#FAF7F2] transition hover:bg-[#2a2a2a] disabled:cursor-not-allowed disabled:bg-[#1A1A1A]/30"
            title={expired ? "This quote has expired. Reach out to ShutterKART Support." : ""}
          >
            Accept this quote
          </button>
          <button
            type="button"
            data-testid="quote-response-ask"
            onClick={onAskFocus}
            className="rounded-full border border-[#1A1A1A]/15 px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] text-[#1A1A1A]/80 transition hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
          >
            Ask a question
          </button>
          <button
            type="button"
            data-testid="quote-response-decline"
            disabled={expired}
            onClick={() => setDeclineOpen(true)}
            className="rounded-full border border-[#1A1A1A]/15 px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] text-[#1A1A1A]/60 transition hover:border-[#1A1A1A] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Decline
          </button>
        </div>
      )}
      <p className="px-6 pb-5 pt-1 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/45 sm:px-7">
        Replies come from ShutterKART Support. No payments are processed on this site.
      </p>

      {acceptOpen ? (
        <ConfirmDialog
          testId="accept-confirm-dialog"
          title="Confirm acceptance"
          body="Our team will contact you to finalize next steps. No payment will be processed here."
          confirmLabel="Confirm acceptance"
          confirmTestId="accept-confirm-yes"
          cancelTestId="accept-confirm-cancel"
          busy={accepting}
          error={acceptError}
          onConfirm={onAccept}
          onCancel={() => setAcceptOpen(false)}
        />
      ) : null}
      {declineOpen ? (
        <DeclineDialog
          quoteId={quote.id}
          onClose={() => setDeclineOpen(false)}
          onDone={async () => {
            setDeclineOpen(false);
            await onChanged?.();
          }}
        />
      ) : null}
    </section>
  );
}

function ConfirmDialog({
  title, body, confirmLabel = "Confirm", cancelLabel = "Cancel",
  busy, error, onConfirm, onCancel, testId, confirmTestId, cancelTestId,
}) {
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-[60] flex items-end justify-center bg-[#1A1A1A]/55 px-4 pb-12 pt-8 sm:items-center sm:p-6"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-xl text-[#1A1A1A]">{title}</h3>
        <p className="mt-2 text-sm text-[#1A1A1A]/70">{body}</p>
        {error ? (
          <p className="mt-3 rounded-lg bg-[#B0413E]/8 px-3 py-2 text-xs text-[#B0413E]">
            {error}
          </p>
        ) : null}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            data-testid={cancelTestId}
            disabled={busy}
            onClick={onCancel}
            className="rounded-full border border-[#1A1A1A]/15 px-4 py-2 text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/70 hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-testid={confirmTestId}
            disabled={busy}
            onClick={onConfirm}
            className="rounded-full bg-[#1A1A1A] px-5 py-2 text-xs uppercase tracking-[0.2em] text-[#FAF7F2] hover:bg-[#2a2a2a] disabled:opacity-60"
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeclineDialog({ quoteId, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      await declineQuote(quoteId, reason || null);
      onDone?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't decline right now.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      data-testid="decline-dialog"
      className="fixed inset-0 z-[60] flex items-end justify-center bg-[#1A1A1A]/55 px-4 pb-12 pt-8 sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-xl text-[#1A1A1A]">Decline this quote</h3>
        <p className="mt-2 text-sm text-[#1A1A1A]/70">
          We'll let our team know. Optionally share why so we can do better.
        </p>
        <textarea
          data-testid="decline-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Optional reason"
          className="mt-3 w-full rounded-2xl border border-[#1A1A1A]/15 bg-[#FAF7F2] px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
        />
        {error ? (
          <p className="mt-3 rounded-lg bg-[#B0413E]/8 px-3 py-2 text-xs text-[#B0413E]">
            {error}
          </p>
        ) : null}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            data-testid="decline-cancel"
            disabled={busy}
            onClick={onClose}
            className="rounded-full border border-[#1A1A1A]/15 px-4 py-2 text-xs uppercase tracking-[0.2em] text-[#1A1A1A]/70 hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="decline-confirm"
            disabled={busy}
            onClick={submit}
            className="rounded-full bg-[#B0413E] px-5 py-2 text-xs uppercase tracking-[0.2em] text-[#FAF7F2] hover:bg-[#9A2D2A] disabled:opacity-60"
          >
            {busy ? "Working…" : "Decline"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conversation thread (WebSocket + polling fallback)
// ---------------------------------------------------------------------------
function ConversationThread({ quote, conversationId, onSendShouldRefreshQuote }) {
  const [messages, setMessages] = useState([]);
  const [opTyping, setOpTyping] = useState(false);
  const [opOnline, setOpOnline] = useState(false);
  const [connState, setConnState] = useState("connecting"); // connecting|open|down
  const [draft, setDraft] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState([]); // [{id,url,mime,filename,size_bytes}]
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const wsRef = useRef(null);
  const opTypingTimerRef = useRef(null);
  const buyerTypingDebounceRef = useRef(null);
  const scrollRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const fileInputRef = useRef(null);
  const accessToken = useMemo(() => getStoredAccessToken(), []);

  // Load initial messages from REST (always — works even when WS is dead)
  useEffect(() => {
    if (!conversationId) return undefined;
    let cancelled = false;
    listConversationMessages(conversationId)
      .then((items) => {
        if (cancelled) return;
        setMessages(items);
        const last = items[items.length - 1];
        if (last) lastMessageIdRef.current = last.id;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // WS connect
  useEffect(() => {
    if (!conversationId || !accessToken) return undefined;
    setConnState("connecting");
    const ws = createConversationWS({
      conversationId,
      token: accessToken,
      onOpen: () => setConnState("open"),
      onClose: () => setConnState("down"),
      onError: () => setConnState("down"),
      onMessage: (m) => {
        setMessages((prev) => {
          if (prev.some((x) => x.id === m.id)) return prev;
          return [...prev, m];
        });
        lastMessageIdRef.current = m.id;
        // If this is a system event flipping the quote, refresh the quote
        // so the rich card updates without a full reload.
        if (
          m.sender_type === "system" &&
          ["quote_accepted", "quote_declined", "quote_expired", "quote_confirmed_offline"].includes(
            m.system_event,
          )
        ) {
          onSendShouldRefreshQuote?.();
        }
      },
      onTyping: (t) => {
        if (t.role !== "operator") return;
        setOpTyping(!!t.is_typing);
        if (opTypingTimerRef.current) clearTimeout(opTypingTimerRef.current);
        if (t.is_typing) {
          opTypingTimerRef.current = setTimeout(() => setOpTyping(false), 4000);
        }
      },
      onPresence: (p) => {
        if (p.role === "operator") setOpOnline(!!p.online);
      },
      onRead: () => {
        /* refresh per-message read state lazily on next list reload */
      },
    });
    wsRef.current = ws;
    return () => {
      if (opTypingTimerRef.current) clearTimeout(opTypingTimerRef.current);
      ws.close();
      wsRef.current = null;
    };
  }, [conversationId, accessToken, onSendShouldRefreshQuote]);

  // Polling fallback when WS is down for > ~ a few seconds
  useEffect(() => {
    if (!conversationId) return undefined;
    if (connState !== "down") return undefined;
    const tick = setInterval(async () => {
      try {
        const items = await listConversationMessages(conversationId, {
          after: lastMessageIdRef.current
            ? // The backend supports `after=<iso>`; use the last known created_at
              messages.find((x) => x.id === lastMessageIdRef.current)?.created_at
            : undefined,
        });
        if (items?.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((p) => p.id));
            const fresh = items.filter((m) => !seen.has(m.id));
            if (!fresh.length) return prev;
            const next = [...prev, ...fresh];
            lastMessageIdRef.current = next[next.length - 1].id;
            return next;
          });
        }
      } catch {
        /* noop */
      }
    }, 15000);
    return () => clearInterval(tick);
  }, [conversationId, connState, messages]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  // Mark last operator message as read once visible
  useEffect(() => {
    if (!messages.length) return;
    const lastOp = [...messages].reverse().find((m) => m.sender_type === "operator");
    if (lastOp && wsRef.current?.isOpen()) {
      wsRef.current.read(lastOp.id);
    }
  }, [messages]);

  const typingDispatch = (val) => {
    if (!wsRef.current?.isOpen()) return;
    wsRef.current.typing(true);
    if (buyerTypingDebounceRef.current) clearTimeout(buyerTypingDebounceRef.current);
    buyerTypingDebounceRef.current = setTimeout(() => {
      wsRef.current?.typing(false);
    }, 1500);
    void val;
  };

  async function onPickFiles(filelist) {
    setUploadError("");
    for (const file of Array.from(filelist || [])) {
      if (file.size > 12 * 1024 * 1024) {
        setUploadError(`${file.name} is over 12 MB.`);
        continue;
      }
      try {
        setUploadProgress(0);
        const out = await uploadConversationAttachment(
          conversationId,
          file,
          setUploadProgress,
        );
        setPendingAttachments((p) => [...p, out]);
      } catch (e) {
        setUploadError(
          e?.response?.data?.detail || `Failed to upload ${file.name}.`,
        );
      } finally {
        setUploadProgress(0);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeAttachment(id) {
    setPendingAttachments((p) => p.filter((a) => a.id !== id));
  }

  async function onSend() {
    const body = draft.trim();
    if (!body && !pendingAttachments.length) return;
    setSending(true);
    setUploadError("");
    try {
      const isFirstAsk =
        quote?.status === "quote_sent" && !pendingAttachments.length;
      if (isFirstAsk && body) {
        // Goes through ask-question to also flip status server-side.
        await askQuoteQuestion(quote.id, body);
        // Refresh quote so status pill updates to conversation_ongoing
        onSendShouldRefreshQuote?.();
      } else if (wsRef.current?.isOpen()) {
        wsRef.current.send(body, pendingAttachments);
      } else {
        await postConversationMessage(conversationId, body, pendingAttachments);
      }
      setDraft("");
      setPendingAttachments([]);
    } catch (e) {
      setUploadError(
        e?.response?.data?.detail || "Couldn't send message right now.",
      );
    } finally {
      setSending(false);
    }
  }

  const grouped = useMemo(() => groupByDay(messages), [messages]);

  const composerDisabled =
    quote?.status === "declined" ||
    quote?.status === "expired" ||
    quote?.status === "confirmed_offline";

  return (
    <>
      <div
        data-testid="quote-detail-conversation"
        className="mt-8 rounded-3xl border border-[#1A1A1A]/10 bg-white p-5 sm:p-6"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="relative grid h-10 w-10 place-items-center rounded-full bg-[#C8A24B]/15 text-[#9A7A2F]">
              <MessageCircle className="h-5 w-5" />
              {opOnline ? (
                <span
                  data-testid="operator-online-dot"
                  className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#4F8060]"
                />
              ) : null}
            </span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#C8A24B]">
                Conversation
              </p>
              <p className="font-display text-xl text-[#1A1A1A]">
                ShutterKART Support
              </p>
            </div>
          </div>
          {connState === "down" ? (
            <span
              data-testid="ws-reconnecting"
              className="rounded-full bg-[#1A1A1A]/8 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/55"
            >
              Reconnecting…
            </span>
          ) : null}
        </div>

        <div
          ref={scrollRef}
          data-testid="message-list"
          className="mt-5 max-h-[55vh] space-y-3 overflow-y-auto pr-1"
        >
          {grouped.length === 0 ? (
            <p className="rounded-2xl bg-[#F5F1E8] px-4 py-3 text-sm text-[#1A1A1A]/65">
              We received your brief. The team will reply right here. You'll
              also get a phone notification.
            </p>
          ) : null}
          {grouped.map((bucket) => (
            <React.Fragment key={bucket.label}>
              <div className="flex items-center justify-center py-1">
                <span className="rounded-full bg-[#1A1A1A]/5 px-3 py-0.5 text-[10px] uppercase tracking-[0.24em] text-[#1A1A1A]/55">
                  {bucket.label}
                </span>
              </div>
              {bucket.items.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}
            </React.Fragment>
          ))}
          {opTyping ? (
            <div
              data-testid="operator-typing"
              className="flex items-center gap-2 pl-1 text-xs text-[#1A1A1A]/55"
            >
              <span className="inline-flex gap-0.5">
                <i className="block h-1.5 w-1.5 animate-bounce rounded-full bg-[#1A1A1A]/45 [animation-delay:-0.3s]" />
                <i className="block h-1.5 w-1.5 animate-bounce rounded-full bg-[#1A1A1A]/45 [animation-delay:-0.15s]" />
                <i className="block h-1.5 w-1.5 animate-bounce rounded-full bg-[#1A1A1A]/45" />
              </span>
              ShutterKART Support is typing…
            </div>
          ) : null}
        </div>
      </div>

      {/* Sticky composer */}
      <div
        data-testid="buyer-composer"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-40 mx-auto max-w-3xl px-3 pb-3 sm:px-6 sm:pb-5"
      >
        <div className="pointer-events-auto rounded-3xl border border-[#1A1A1A]/12 bg-white p-3 shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.18)] sm:p-4">
          {composerDisabled ? (
            <p className="px-2 py-2 text-xs text-[#1A1A1A]/60">
              This thread is closed because the quote is{" "}
              {(quote?.status || "").replaceAll("_", " ")}. Email{" "}
              <a
                href="mailto:hello@shutterkart.com"
                className="font-medium text-[#1A1A1A] underline"
              >
                hello@shutterkart.com
              </a>{" "}
              to re-open.
            </p>
          ) : (
            <>
              {pendingAttachments.length || uploadProgress ? (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  {pendingAttachments.map((a) => (
                    <span
                      key={a.id}
                      data-testid={`pending-attachment-${a.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F1E8] px-3 py-1 text-xs text-[#1A1A1A]"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      <span className="max-w-[140px] truncate">{a.filename}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.id)}
                        className="ml-1 text-[#1A1A1A]/55 hover:text-[#1A1A1A]"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {uploadProgress > 0 && uploadProgress < 100 ? (
                    <span className="rounded-full bg-[#1A1A1A]/5 px-3 py-1 text-xs text-[#1A1A1A]/65">
                      Uploading {uploadProgress}%
                    </span>
                  ) : null}
                </div>
              ) : null}
              {uploadError ? (
                <p className="mb-2 px-2 text-xs text-[#B0413E]">{uploadError}</p>
              ) : null}
              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                  className="hidden"
                  onChange={(e) => onPickFiles(e.target.files)}
                  data-testid="buyer-attach-input"
                />
                <button
                  type="button"
                  data-testid="buyer-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  className="grid h-10 w-10 place-items-center rounded-full border border-[#1A1A1A]/15 text-[#1A1A1A]/70 hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
                  aria-label="Attach files"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
                <textarea
                  id="buyer-composer-input"
                  data-testid="buyer-composer-input"
                  rows={1}
                  value={draft}
                  placeholder="Reply to ShutterKART Support…"
                  onChange={(e) => {
                    setDraft(e.target.value);
                    typingDispatch(e.target.value);
                    const el = e.target;
                    el.style.height = "auto";
                    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      onSend();
                    }
                  }}
                  className="min-h-[40px] flex-1 resize-none rounded-2xl border border-[#1A1A1A]/12 bg-[#FAF7F2] px-3 py-2 text-sm text-[#1A1A1A] focus:border-[#C8A24B] focus:outline-none"
                />
                <button
                  type="button"
                  data-testid="buyer-send-btn"
                  disabled={sending || (!draft.trim() && !pendingAttachments.length)}
                  onClick={onSend}
                  className="grid h-10 w-10 place-items-center rounded-full bg-[#1A1A1A] text-[#FAF7F2] transition hover:bg-[#2a2a2a] disabled:cursor-not-allowed disabled:bg-[#1A1A1A]/30"
                  aria-label="Send message"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function MessageBubble({ message }) {
  const ts = formatTimeShort(message.created_at);
  if (message.sender_type === "system") {
    return (
      <div
        data-testid={`message-system-${message.id}`}
        className="mx-auto max-w-md rounded-full bg-[#1A1A1A]/5 px-3 py-1 text-center text-[11px] text-[#1A1A1A]/65"
      >
        {message.body || message.system_event?.replaceAll("_", " ")}
      </div>
    );
  }
  const isBuyer = message.sender_type === "buyer";
  return (
    <div
      data-testid={`message-${message.sender_type}-${message.id}`}
      className={`flex ${isBuyer ? "justify-end" : "justify-start"}`}
    >
      <div
        className={[
          "max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm",
          isBuyer
            ? "bg-[#C8A24B]/15 text-[#1A1A1A] rounded-tr-sm"
            : "bg-white text-[#1A1A1A] border border-[#1A1A1A]/8 rounded-tl-sm",
        ].join(" ")}
      >
        {!isBuyer ? (
          <p className="mb-0.5 text-[10px] uppercase tracking-[0.22em] text-[#9A7A2F]">
            ShutterKART Support
          </p>
        ) : null}
        {message.body ? (
          <p className="whitespace-pre-wrap leading-relaxed">{message.body}</p>
        ) : null}
        {message.attachments?.length ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.attachments.map((a, i) => (
              <Attachment key={a.id || i} attachment={a} />
            ))}
          </div>
        ) : null}
        <div className="mt-1 flex items-center justify-end gap-2 text-[10px] text-[#1A1A1A]/45">
          <span>{ts}</span>
          {isBuyer && (message.read_by || []).some((r) => r.role === "operator") ? (
            <span
              data-testid={`seen-${message.id}`}
              className="inline-flex items-center gap-0.5 text-[#4F8060]"
            >
              <CheckCircle2 className="h-3 w-3" />
              Seen
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Attachment({ attachment }) {
  const url = absoluteUrl(attachment.url);
  if ((attachment.mime || "").startsWith("image/")) {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid={`attachment-image-${attachment.id || ""}`}>
        <img
          src={url}
          alt={attachment.filename}
          className="max-h-60 max-w-full rounded-xl border border-[#1A1A1A]/10 object-cover"
        />
      </a>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={`attachment-file-${attachment.id || ""}`}
      className="inline-flex items-center gap-2 rounded-xl border border-[#1A1A1A]/12 bg-white px-3 py-2 text-xs text-[#1A1A1A] hover:border-[#1A1A1A]/30"
    >
      <FileText className="h-4 w-4 text-[#9A7A2F]" />
      <span className="max-w-[160px] truncate">{attachment.filename}</span>
      <span className="text-[#1A1A1A]/50">
        {attachment.size_bytes ? `${Math.ceil(attachment.size_bytes / 1024)} KB` : ""}
      </span>
    </a>
  );
}

function groupByDay(messages) {
  const out = [];
  let current = null;
  for (const m of messages) {
    const label = dateBucket(m.created_at);
    if (!current || current.label !== label) {
      current = { label, items: [] };
      out.push(current);
    }
    current.items.push(m);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Collapsible summary
// ---------------------------------------------------------------------------
function CollapsibleSummary({ open, setOpen, quote, scope, refs, payload }) {
  return (
    <div
      data-testid="quote-detail-summary-card"
      className="mt-6 overflow-hidden rounded-3xl border border-[#1A1A1A]/10 bg-white"
    >
      <button
        type="button"
        data-testid="quote-detail-summary-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition hover:bg-[#1A1A1A]/[0.02] sm:px-6"
      >
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C8A24B]">
            Your submitted request
          </p>
          <p className="mt-0.5 font-display text-lg text-[#1A1A1A]">
            Review the brief
          </p>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-[#1A1A1A]/60 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <dl className="space-y-3 border-t border-[#1A1A1A]/10 px-5 py-5 text-sm sm:px-6">
          <Row label="Occasion" value={quote?.occasion} />
          {(quote?.occasion_subtypes || []).length ? (
            <Row label="Subtypes" value={(quote?.occasion_subtypes || []).join(", ")} />
          ) : null}
          <Row label="Date" value={quote?.date_summary} />
          <Row label="Location" value={quote?.location_summary} />
          {scope.guest_count_band ? (
            <Row label="Guests" value={scope.guest_count_band} />
          ) : null}
          {scope.duration ? <Row label="Duration" value={scope.duration} /> : null}
          {scope.coverage_style ? (
            <Row
              label="Coverage"
              value={Array.isArray(scope.coverage_style) ? scope.coverage_style.join(", ") : scope.coverage_style}
            />
          ) : null}
          {scope.drone ? <Row label="Drone" value={scope.drone} /> : null}
          {scope.video_deliverables ? (
            <Row label="Video" value={scope.video_deliverables} />
          ) : null}
          {scope.album ? <Row label="Album" value={scope.album} /> : null}
          {refs.length ? (
            <Row label="References" value={`${refs.length} attached`} />
          ) : null}
          {quote?.estimated_range_low != null && quote?.estimated_range_high != null ? (
            <Row
              label="Estimated range"
              value={`${formatINR(quote.estimated_range_low)} – ${formatINR(quote.estimated_range_high)}`}
            />
          ) : null}
          {payload?.contact?.full_name ? (
            <Row label="Name" value={payload.contact.full_name} />
          ) : null}
          {payload?.contact?.email ? (
            <Row label="Email" value={payload.contact.email} />
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}

function Row({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[#1A1A1A]/5 pb-3 last:border-0 last:pb-0">
      <dt className="text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/55">
        {label}
      </dt>
      <dd className="max-w-[60%] text-right capitalize text-[#1A1A1A]">
        {String(value).replaceAll("_", " ")}
      </dd>
    </div>
  );
}


// ---------------------------------------------------------------------------
// BuyerTaxBreakdown — collapsible "Inclusive of GST" expander shown beneath
// the total amount. Numbers come from the buyer-safe tax_breakup the API
// already redacted (no seller_state, no tax_mode_override).
// ---------------------------------------------------------------------------
function BuyerTaxBreakdown({ tb }) {
  const [open, setOpen] = useState(false);
  if (!tb) return null;
  const isIntra = tb.tax_mode === "intra";
  const rows = isIntra
    ? [
        { label: "Taxable base", value: tb.taxable_base },
        { label: `CGST (${tb.cgst_percent_applied}%)`, value: tb.cgst_amount },
        { label: `SGST (${tb.sgst_percent_applied}%)`, value: tb.sgst_amount },
      ]
    : [
        { label: "Taxable base", value: tb.taxable_base },
        { label: `IGST (${tb.igst_percent_applied}%)`, value: tb.igst_amount },
      ];
  return (
    <div className="mt-3" data-testid="buyer-tax-breakdown">
      <button
        type="button"
        data-testid="buyer-tax-breakdown-toggle"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-[11px] text-[#1A1A1A]/65 hover:text-[#9A7A2F]"
      >
        Inclusive of GST · {open ? "Hide" : "View"} breakdown
        <span aria-hidden>{open ? "▴" : "▾"}</span>
      </button>
      {open ? (
        <div
          data-testid="buyer-tax-breakdown-body"
          className="mt-2 space-y-1 rounded-2xl border border-[#0F172A]/8 bg-white px-4 py-3 text-xs text-[#1A1A1A]/80"
        >
          {rows.map((r) => (
            <div key={r.label} className="flex justify-between gap-3">
              <span>{r.label}</span>
              <span>{formatINR(r.value)}</span>
            </div>
          ))}
          <div className="mt-2 flex justify-between border-t border-[#0F172A]/8 pt-2 text-[#1A1A1A] font-medium">
            <span>Total (inclusive)</span>
            <span>{formatINR(tb.total_gross)}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

